/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { Observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { AutoTaggingJobStore } from 'src/customText/core/stores/CustomTextAutoTaggingJobStore';
import { AutoTaggingJobItem } from 'src/customText/verticals/autotagTriggerModal/types';

export type AutoTaggingJobTableProps = TableProps<AutoTaggingJobItem> & {
    onModelClick: (modelId: string) => void;
    onAutoTaggingJobClick: (jobId: string) => void;
};

const messages = defineMessages({
    status: { id: 'customText.autoTaggingJobTable.status', defaultMessage: 'Status' },
    jobFailed: { id: 'customText.autoTaggingJobTable.jobFailed', defaultMessage: 'failed' },
    jobId: { id: 'customText.autoTaggingJobTable.jobId', defaultMessage: 'Auto-label job id' },
    triggered: { id: 'customText.autoTaggingJobTable.triggered', defaultMessage: 'Triggered on' },
    inProgress: { id: 'customText.autoTaggingJobTable.inProgress', defaultMessage: 'In progress' },
    jobSucceeded: { id: 'customText.autoTaggingJobTable.jobSucceeded', defaultMessage: 'succeeded' },
    trainedModelName: { id: 'customText.autoTaggingJobTable.trainedModelName', defaultMessage: 'Trained model name' },
    emptyTablePlaceholder: { id: 'customText.autoTaggingJobTable.emptyTablePlaceholder', defaultMessage: `No items found.` },
    jobNotStartedDescription: {
        id: 'customText.autoTaggingJobTable.jobNotStartedDescription',
        defaultMessage: 'Job in queue. Starting soon ... '
    }
});

const theme = getScopedTheme('TrainingJobTable');
const tableColumnTheme = getScopedTheme('AutotagJobTableColumn');

export const AutoTaggingJobTable = (props: AutoTaggingJobTableProps) => {
    const { sortKey, onModelClick, onAutoTaggingJobClick, ...rest } = props;
    const { localize } = useLocalization();

    const onRenderStatus = (item: AutoTaggingJobStore, _index: number) => {
        switch (item.status) {
            case 'succeeded':
                return (
                    <Stack horizontal verticalAlign="baseline" tokens={{ childrenGap: 4 }}>
                        <FontIcon iconName="Completed" style={{ color: theme.successColor }} />
                        <LuisText size={tableColumnTheme.fontSize.toString()}>{localize(messages.jobSucceeded)}</LuisText>
                    </Stack>
                );
            case 'notStarted':
            case 'inProgress':
                const description =
                    item.status === 'notStarted' ? localize(messages.jobNotStartedDescription) : localize(messages.inProgress);

                return (
                    <Stack horizontal verticalAlign="baseline" tokens={{ childrenGap: 4 }}>
                        <Spinner size={SpinnerSize.xSmall} />
                        <LuisText size={tableColumnTheme.fontSize.toString()}>{description}</LuisText>
                    </Stack>
                );
            case 'failed':
            default:
                return (
                    <Stack horizontal verticalAlign="baseline" tokens={{ childrenGap: 4 }}>
                        <FontIcon iconName="Warning" style={{ color: theme.failureColor }} />
                        <LuisText size={tableColumnTheme.fontSize.toString()}>{localize(messages.jobFailed)}</LuisText>
                    </Stack>
                );
        }
    };

    const columns: IColumn[] = [
        {
            key: 'jobId',
            minWidth: 150,
            maxWidth: 300,
            fieldName: 'id',
            isRowHeader: true,
            isResizable: true,
            name: localize(messages.jobId),
            isSorted: sortKey && !!(sortKey.key === 'id'),
            isSortedDescending: sortKey && !!(sortKey.key === 'id') && sortKey?.order === 'desc',
            onRender: (item: AutoTaggingJobStore) => (
                <NameCellLink onClick={() => onAutoTaggingJobClick(item.id)}>{item.id ?? '--'}</NameCellLink>
            )
        },
        {
            minWidth: 150,
            maxWidth: 300,
            isResizable: true,
            key: 'trainedModelName',
            fieldName: 'trainedModelLabel',
            name: localize(messages.trainedModelName),
            isSorted: sortKey && !!(sortKey.key === 'trainedModelLabel'),
            isSortedDescending: sortKey && !!(sortKey.key === 'trainedModelLabel') && sortKey?.order === 'desc',
            onRender: (item: AutoTaggingJobStore) => (
                <Observer>
                    {() =>
                        Boolean(item.trainedModelLabel) ? (
                            <NameCellLink onClick={() => onModelClick(item.trainedModelLabel)}>{item.trainedModelLabel}</NameCellLink>
                        ) : (
                            <LuisText>Azure OpenAI’s GPT</LuisText>
                        )
                    }
                </Observer>
            )
        },
        {
            key: 'status',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            fieldName: 'status',
            name: localize(messages.status),
            isSorted: sortKey && !!(sortKey.key === 'status'),
            onRender: (item, index) => <Observer>{() => onRenderStatus(item, index)}</Observer>,
            isSortedDescending: sortKey && !!(sortKey.key === 'status') && sortKey?.order === 'desc'
        },
        {
            minWidth: 50,
            maxWidth: 100,
            isResizable: true,
            key: 'triggeredOn',
            fieldName: 'createdDateTime',
            name: localize(messages.triggered),
            isSorted: sortKey && !!(sortKey.key === 'createdDateTime'),
            isSortedDescending: sortKey && !!(sortKey.key === 'createdDateTime') && sortKey?.order === 'desc',
            onRender: (item: AutoTaggingJobStore) => (
                <LuisText size={tableColumnTheme.fontSize.toString()}>{item.createdDateTime?.toLocaleDateString() ?? '--'}</LuisText>
            )
        }
    ];

    return (
        <Table<AutoTaggingJobItem>
            columns={columns}
            selectionMode={SelectionMode.none}
            emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
            {...rest}
        />
    );
};
